import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import SystemStatus from "../components/SystemStatus";
import HotLeads from "../components/HotLeads";
import TeleprospectionPanel from "../components/admin/TeleprospectionPanel";
import BusinessesPanel from "../components/admin/BusinessesPanel";
import OverviewPanel from "../components/admin/OverviewPanel";
import ImportPanel from "../components/admin/ImportPanel";
import OutreachPanel from "../components/admin/OutreachPanel";
import ReviewsPanel from "../components/admin/ReviewsPanel";
import SearchMissesPanel from "../components/admin/SearchMissesPanel";
import HeroAbPanel from "../components/admin/HeroAbPanel";
import SiteWebLeadsPanel from "../components/admin/SiteWebLeadsPanel";
import EmailDiagPanel from "../components/admin/EmailDiagPanel";
import OptOutsPanel from "../components/admin/OptOutsPanel";
import TodayPanel from "../components/admin/TodayPanel";

export default function Admin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [tab, setTab] = useState("today");
  const [advancedMode, setAdvancedMode] = useState(() => localStorage.getItem("mc68_admin_advanced") === "1");
  const [teleproNewCount, setTeleproNewCount] = useState(0);
  const [teleproLatest, setTeleproLatest] = useState([]);

  useEffect(() => {
    localStorage.setItem("mc68_admin_advanced", advancedMode ? "1" : "0");
  }, [advancedMode]);

  const load = async () => {
    try {
      const r = await api.get("/admin/dashboard");
      setData(r.data);
      const p = await api.get("/admin/reviews/pending");
      setPending(p.data);
    } catch (e) {
      console.error("admin dashboard load failed:", e);
    }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  // Telepro signup notifications — badge with count of new signups since last view.
  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/telepro/new-count")
      .then((r) => { setTeleproNewCount(r.data.count || 0); setTeleproLatest(r.data.latest || []); })
      .catch(() => {});
  }, [user]);
  useEffect(() => {
    if (tab === "telepro" && teleproNewCount > 0) {
      api.post("/admin/telepro/mark-seen")
        .then(() => setTeleproNewCount(0))
        .catch(() => {});
    }
  }, [tab, teleproNewCount]);

  // If user disables advancedMode while on an advanced tab, snap back to overview.
  useEffect(() => {
    const advancedOnly = ["system", "hero_ab", "search_misses", "import", "outreach", "email_diag", "opt_outs"];
    if (!advancedMode && advancedOnly.includes(tab)) setTab("today");
  }, [advancedMode, tab]);

  if (!user) return <div className="p-20 text-center" data-testid="admin-not-logged">Connectez-vous.</div>;
  if (user.role !== "admin") return <div className="p-20 text-center text-muted-foreground" data-testid="admin-forbidden">Accès admin requis.</div>;
  if (!data) return <div className="p-20 text-center text-muted-foreground">Chargement…</div>;

  const tabs = [
    ["today", "🎯 Aujourd'hui", false],
    ["overview", "Vue d'ensemble", false],
    ["telepro", `📞 Téléprospection${teleproNewCount > 0 ? ` (${teleproNewCount})` : ""}`, false],
    ["businesses", "📋 Annuaire complet", false],
    ["hot_leads", "🔥 Leads chauds", false],
    ["site_web_leads", "🌐 Leads sites web", false],
    ["reviews", `Modération (${pending.length})`, false],
    ["email_diag", "📧 Emails", true],
    ["opt_outs", "🚫 Opt-outs", true],
    ["system", "État du système", true],
    ["hero_ab", "🎯 Test A/B/C Home", true],
    ["search_misses", "🔎 Recherches vides", true],
    ["import", "Import", true],
    ["outreach", "Outreach IA", true],
  ].filter(([, , adv]) => !adv || advancedMode);

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="label-uppercase mb-2">Administration</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">Console admin</h1>
          <p className="text-xs text-muted-foreground mt-1">🤖 L&apos;app tourne en pilote auto. Aucune action quotidienne requise.</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none mt-2" data-testid="admin-advanced-toggle" title="Affiche les boutons de déclenchement manuel (Importer maintenant, Lancer Outreach, Scraper, Rescan) — utile pour debug ou tests ponctuels">
          <input type="checkbox" checked={advancedMode} onChange={(e) => setAdvancedMode(e.target.checked)} className="w-3 h-3 accent-primary" />
          Mode avancé
        </label>
      </div>

      <div className="flex flex-wrap gap-1 mb-8 border-b border-border overflow-x-auto">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`} data-testid={`admin-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "today" && <TodayPanel />}
      {tab === "overview" && (
        <OverviewPanel
          data={data}
          teleproNewCount={teleproNewCount}
          teleproLatest={teleproLatest}
          onGoToTelepro={() => setTab("telepro")}
          onGoToSearchMisses={() => { setAdvancedMode(true); setTab("search_misses"); }}
          onReload={load}
        />
      )}
      {tab === "system" && <SystemStatus />}
      {tab === "hot_leads" && <HotLeads />}
      {tab === "site_web_leads" && <SiteWebLeadsPanel />}
      {tab === "telepro" && <TeleprospectionPanel />}
      {tab === "businesses" && <BusinessesPanel />}
      {tab === "search_misses" && <SearchMissesPanel />}
      {tab === "hero_ab" && <HeroAbPanel />}
      {tab === "import" && <ImportPanel data={data} advancedMode={advancedMode} onReload={load} />}
      {tab === "outreach" && <OutreachPanel advancedMode={advancedMode} />}
      {tab === "email_diag" && <EmailDiagPanel />}
      {tab === "opt_outs" && <OptOutsPanel />}
      {tab === "reviews" && (
        <ReviewsPanel
          pending={pending}
          onModerated={(id) => setPending((prev) => prev.filter((r) => r.id !== id))}
        />
      )}
    </div>
  );
}
